import { useState } from "react";
import { Link } from "wouter";
import { Banknote, Truck, Wallet } from "lucide-react";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { origemAtual } from "@/lib/navegacao";
import { usePode } from "@/lib/permissoes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/layout/page-header";
import { Segmented } from "@/components/layout/segmented";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { EmptyState } from "@/components/data/empty-state";
import { CompradorShell } from "@/components/mercado/produtor-shell";
import { ChipPagamento, StatusChip } from "@/components/mercado/status-chip";
import { Tile, Numerao, ChipsDeSituacao, AvatarProdutor } from "@/components/mercado/painel";
import { DialogPagamentoLote } from "@/components/mercado/dialog-pagamento-lote";
import { useToast } from "@/hooks/use-toast";
import { useAPagar, useInformarPagamentoDaCarga } from "@/hooks/use-cargas";
import { useCarteira } from "@/hooks/use-romaneios";
import { fmtMoedaBR, fmtKgBR, fmtDataCurtaBR, fmtDiaCurto, fmtIntervaloCurto, fmtMesLongo } from "@/lib/formato-mercado";
import { cn } from "@/lib/utils";
import { diaCivilNaFazenda } from "@shared/data-fazenda";
import { FORMA_LANCAMENTO_LABEL, type FormaLancamento } from "@shared/schema";
import {
  AGRUPAMENTOS_PAGAR,
  ORIGENS_PAGAR,
  SITUACOES_PAGAR,
  type AgrupamentoPagar,
  type APagarDTO,
  type FiltroAPagar,
  type GrupoListaPagarDTO,
  type LinhaListaPagarDTO,
  type OrigemPagar,
  type SituacaoPagar,
} from "@shared/mercado-comprador";
import { AVISO_PAGAR_CARGA } from "@shared/textos-pagamento";

/**
 * "A pagar" — o que o comprador deve aos produtores.
 *
 * ⚠️ QUEM REGISTRA QUITA. Registrar aqui fecha o documento para os dois lados
 * na hora — não existe "aguardando o produtor", nem chip, nem KPI disso. O botão
 * é "Registrar pagamento" em toda linha, do Rizoma ou de registro próprio.
 *
 * ⚠️ O APP NÃO MANDA MENSAGEM. Nenhum texto daqui promete aviso.
 *
 * ⚠️ Nenhum total é calculado aqui. KPIs, contagens dos chips, subtotais dos
 * grupos e o rodapé vêm do servidor (`linhasAPagar` com os filtros). A única
 * soma da tela é a da SELEÇÃO — que é o que o corretor marcou agora, e o total
 * que vale é o que o servidor devolve depois de gravar.
 *
 * ⚠️ FILTRO NA URL (CLAUDE.md §4): chip, produtor, origem, agrupamento, busca e
 * a janela dos pagos. `/mercado/pagar?produtor=<id>` abre filtrado. A SELEÇÃO
 * fica fora — é UI do momento.
 */

// Constantes de MÓDULO (deps de useMemo dentro do hook).
const PADRAO = { situacao: "ABERTO", produtor: "todos", origem: "todas", agrupar: "vencimento", q: "", de: "", ate: "" };
const PERMITIDOS = {
  situacao: SITUACOES_PAGAR as readonly string[],
  origem: ORIGENS_PAGAR as readonly string[],
  agrupar: AGRUPAMENTOS_PAGAR as readonly string[],
};

const ROTULO_CHIP: Record<SituacaoPagar, string> = {
  ABERTO: "Em aberto",
  VENCIDO: "Vencidos",
  SEMANA: "Esta semana",
  PROXIMOS: "Próximos",
  SEM_VENCIMENTO: "Sem vencimento",
  PAGO: "Pagos",
};

const OPCOES_AGRUPAR: { id: AgrupamentoPagar; label: string }[] = [
  { id: "vencimento", label: "Vencimento" },
  { id: "produtor", label: "Produtor" },
  { id: "nenhum", label: "Nenhum" },
];

/** O filtro do servidor a partir do estado da URL — puro, para o teste de contrato da tela. */
export function filtroDaUrl(e: typeof PADRAO): FiltroAPagar {
  return {
    situacao: e.situacao as SituacaoPagar,
    produtorContatoId: e.produtor !== "todos" ? e.produtor : undefined,
    origem: e.origem as OrigemPagar,
    agrupar: e.agrupar as AgrupamentoPagar,
    q: e.q || undefined,
    pagosDe: e.de || undefined,
    pagosAte: e.ate || undefined,
  };
}

export default function MercadoAPagar() {
  const { estado, definir, limpar } = useEstadoNaUrl(PADRAO, PERMITIDOS);
  const podeOperar = usePode("mercado.corretor.operar");
  const consulta = useAPagar(filtroDaUrl(estado));
  const { data } = consulta;
  const aguardando = estadoDaConsulta(consulta, { variante: "painel" });
  const { data: carteira } = useCarteira(true);

  const [selecao, setSelecao] = useState<Record<string, LinhaListaPagarDTO>>({});
  const [pagando, setPagando] = useState<LinhaListaPagarDTO[] | null>(null);
  const [escolhendoCarga, setEscolhendoCarga] = useState(false);
  const [cargaParaPagar, setCargaParaPagar] = useState<APagarDTO["cargasComSaldo"][number] | null>(null);

  const selecionadas = Object.keys(selecao).map(k => selecao[k]);
  const alternar = (linhas: LinhaListaPagarDTO[], marcar: boolean) => {
    const nova = { ...selecao };
    for (const l of linhas) {
      if (!l.selecionavel) continue;
      if (marcar) nova[l.alvoId] = l; else delete nova[l.alvoId];
    }
    setSelecao(nova);
  };
  const filtrando = estado.produtor !== "todos" || estado.origem !== "todas" || !!estado.q;

  return (
    <CompradorShell className={cn("space-y-4", selecionadas.length > 0 && "pb-24")}>
      <PageHeader
        titulo="A pagar"
        descricao="O que você deve aos produtores. Registrar o pagamento quita o documento para os dois lados."
        empilharQuandoEstreito
        acoesSecundarias={podeOperar && data && data.cargasComSaldo.length > 0 ? (
          <Button variant="outline" onClick={() => setEscolhendoCarga(true)} data-testid="button-pagar-carga-inteira">
            <Truck className="h-4 w-4 mr-1.5" />Pagar uma carga inteira
          </Button>
        ) : undefined}
      />

      {aguardando || !data ? aguardando : (
        <>
          <Tiles dados={data} onVerVencidos={() => definir({ situacao: "VENCIDO" })} />

          <Avisos dados={data} podeOperar={podeOperar} onPagarCarga={setCargaParaPagar} />

          <Filtros
            estado={estado}
            definir={definir}
            contagens={data.lista.contagens}
            carteira={(carteira ?? []).map(c => ({ id: c.id, nome: c.nome }))}
          />

          {data.lista.grupos.length === 0 ? (
            <EmptyState
              icone={Wallet}
              titulo={estado.situacao === "PAGO" ? "Nenhum pagamento nesse período." : filtrando ? "Nada com esses filtros." : "Nada a pagar."}
              descricao={estado.situacao === "PAGO"
                ? "Os pagamentos aparecem aqui pela data em que o dinheiro saiu."
                : filtrando ? "Tente outro produtor, outra origem ou limpe a busca." : "Quando você registrar um romaneio ou receber um do Rizoma, a dívida aparece aqui."}
              acao={filtrando ? <Button variant="outline" onClick={limpar} data-testid="button-limpar-filtros">Limpar filtros</Button> : undefined}
            />
          ) : (
            <>
              <TabelaAPagar
                dados={data}
                selecao={selecao}
                podeOperar={podeOperar}
                onAlternar={alternar}
                onPagar={l => setPagando([l])}
              />
              <ListaCompacta
                dados={data}
                selecao={selecao}
                podeOperar={podeOperar}
                onAlternar={alternar}
                onPagar={l => setPagando([l])}
              />
            </>
          )}
        </>
      )}

      {selecionadas.length > 0 && (
        <BarraDeSelecao
          linhas={selecionadas}
          onLimpar={() => setSelecao({})}
          onPagar={() => setPagando(selecionadas)}
        />
      )}

      <DialogPagamentoLote linhas={pagando} onFechar={() => setPagando(null)} onPago={() => setSelecao({})} />
      <DialogEscolherCarga
        aberto={escolhendoCarga}
        cargas={data?.cargasComSaldo ?? []}
        onFechar={() => setEscolhendoCarga(false)}
        onEscolher={c => { setEscolhendoCarga(false); setCargaParaPagar(c); }}
      />
      <DialogPagarCarga carga={cargaParaPagar} onFechar={() => setCargaParaPagar(null)} />
    </CompradorShell>
  );
}

// ─── Os quatro números ───────────────────────────────────────────────────────

function Tiles({ dados, onVerVencidos }: { dados: APagarDTO; onVerVencidos: () => void }) {
  const k = dados.kpis;
  const mes = fmtMesLongo(dados.lista.pagos.ate).toUpperCase();
  return (
    // Os KPIs são a foto da dívida inteira: NÃO mudam com filtro (o servidor garante).
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tiles-a-pagar">
      <Tile rotulo="Em aberto">
        <Numerao valor={fmtMoedaBR(k.emAberto)} testId="tile-a-pagar-aberto" />
        <p className="text-xs text-muted-foreground">
          {plural(k.emAbertoDocumentos, "documento", "documentos")} · {plural(k.emAbertoProdutores, "produtor", "produtores")}
        </p>
      </Tile>
      <Tile rotulo="Vencido" perigo={k.vencido > 0}>
        <Numerao valor={fmtMoedaBR(k.vencido)} className={k.vencido > 0 ? "text-destructive" : undefined} testId="tile-a-pagar-vencido" />
        <p className="text-xs text-muted-foreground">
          {k.vencido > 0 ? plural(k.vencidoDocumentos, "documento", "documentos") : "nada vencido"}
        </p>
        {k.vencido > 0 && (
          <button type="button" onClick={onVerVencidos} className="mt-auto self-start text-xs font-semibold text-destructive" data-testid="link-ver-vencidos">
            Ver vencidos →
          </button>
        )}
      </Tile>
      <Tile rotulo="Vence esta semana" alerta={k.venceSemana > 0}>
        <Numerao valor={fmtMoedaBR(k.venceSemana)} testId="tile-a-pagar-semana" />
        <p className="text-xs text-muted-foreground">até {fmtDiaCurto(dados.janelaSemana.fim)}</p>
      </Tile>
      <Tile rotulo={`Pago em ${mes}`}>
        <Numerao valor={fmtMoedaBR(k.pagoNoMes)} testId="tile-a-pagar-pago-mes" />
        <p className="text-xs text-muted-foreground">pela data do dinheiro</p>
      </Tile>
    </div>
  );
}

/** Excedentes e cargas com saldo: compactos, acima da lista. */
function Avisos({
  dados, podeOperar, onPagarCarga,
}: {
  dados: APagarDTO;
  podeOperar: boolean;
  onPagarCarga: (c: APagarDTO["cargasComSaldo"][number]) => void;
}) {
  if (dados.excedentes.length === 0 && dados.cargasComSaldo.length === 0) return null;
  return (
    <div className="space-y-2">
      {dados.excedentes.map(l => (
        <p key={l.alvoId} className="rounded-md border border-atencao/40 bg-atencao-suave px-3 py-2 text-xs" data-testid={`excedente-${l.alvoId}`}>
          <b>{l.documento} · {l.produtorNome}</b>: pago {fmtMoedaBR(l.excedenteValor)} a mais. Acerte com o produtor.
        </p>
      ))}
      {dados.cargasComSaldo.map(c => (
        <div key={c.cargaId} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm" data-testid={`card-carga-saldo-${c.identificador}`}>
          <Truck className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-mono font-semibold">{c.identificador}</span>
          <span className="text-muted-foreground text-xs">
            {c.entregueEm ? `entregue ${fmtDataCurtaBR(c.entregueEm.substring(0, 10))} · ` : ""}
            {plural(c.nProdutores, "produtor", "produtores")} · <span className="font-mono">{fmtMoedaBR(c.saldo)}</span> em aberto
          </span>
          {podeOperar && (
            <Button size="sm" variant="outline" className="ml-auto" onClick={() => onPagarCarga(c)} data-testid={`button-pagar-carga-${c.identificador}`}>
              Pagar a carga
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Filtros ─────────────────────────────────────────────────────────────────

function Filtros({
  estado, definir, contagens, carteira,
}: {
  estado: typeof PADRAO;
  definir: (p: Partial<typeof PADRAO>) => void;
  contagens: Record<SituacaoPagar, number>;
  carteira: { id: string; nome: string }[];
}) {
  return (
    <div className="space-y-2.5">
      <ChipsDeSituacao<string>
        opcoes={SITUACOES_PAGAR.map(s => ({ id: s, label: ROTULO_CHIP[s], n: contagens[s] }))}
        valor={estado.situacao}
        onChange={s => definir({ situacao: s })}
        testId="chips-a-pagar"
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <Select value={estado.produtor} onValueChange={v => definir({ produtor: v })}>
            <SelectTrigger className="sm:w-52" data-testid="filtro-produtor-pagar"><SelectValue placeholder="Produtor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os produtores</SelectItem>
              {carteira.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={estado.origem} onValueChange={v => definir({ origem: v })}>
            <SelectTrigger className="sm:w-44" data-testid="filtro-origem-pagar"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas as origens</SelectItem>
              <SelectItem value="RIZOMA">Romaneio do Rizoma</SelectItem>
              <SelectItem value="REGISTRO_PROPRIO">Registro próprio</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Agrupar</span>
          <Segmented<AgrupamentoPagar>
            valor={estado.agrupar as AgrupamentoPagar}
            onChange={v => definir({ agrupar: v })}
            opcoes={OPCOES_AGRUPAR}
            testId="seg-agrupar-pagar"
          />
        </div>
        <Input
          value={estado.q}
          onChange={e => definir({ q: e.target.value })}
          placeholder="Buscar produtor, documento ou carga"
          aria-label="Buscar por produtor, documento ou carga"
          className="sm:ml-auto sm:w-64"
          data-testid="busca-pagar"
        />
      </div>
      {estado.situacao === "PAGO" && (
        <div className="flex flex-wrap items-end gap-2" data-testid="janela-pagos">
          <div className="space-y-1">
            <Label htmlFor="pagos-de" className="text-xs">Pagos de</Label>
            <Input id="pagos-de" type="date" value={estado.de} onChange={e => definir({ de: e.target.value })} className="w-40" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pagos-ate" className="text-xs">até</Label>
            <Input id="pagos-ate" type="date" value={estado.ate} onChange={e => definir({ ate: e.target.value })} className="w-40" />
          </div>
          <p className="text-xs text-muted-foreground pb-2">Vazio = o mês atual, pela data do dinheiro.</p>
        </div>
      )}
    </div>
  );
}

// ─── Tabela (desktop) ────────────────────────────────────────────────────────

function tituloDoGrupo(g: GrupoListaPagarDTO, dados: APagarDTO): string | null {
  if (g.chave === "SEMANA") return `Esta semana · ${fmtIntervaloCurto(dados.janelaSemana.inicio, dados.janelaSemana.fim)}`;
  return g.titulo;
}

function Vence({ l }: { l: LinhaListaPagarDTO }) {
  if (l.situacao === "PAGO") {
    return (
      <span className="block leading-tight">
        <b className="font-medium whitespace-nowrap">{fmtDiaCurto(l.pagoEm)}</b>
        <span className="block text-xs text-muted-foreground">{l.forma ? FORMA_LANCAMENTO_LABEL[l.forma as FormaLancamento] ?? l.forma : "—"}</span>
      </span>
    );
  }
  if (!l.vencimento) return <span className="text-muted-foreground">sem data</span>;
  return (
    <span className="block leading-tight">
      <b className={cn("font-medium whitespace-nowrap", l.situacao === "VENCIDO" && "text-destructive")}>{fmtDiaCurto(l.vencimento)}</b>
      {l.dias != null && l.dias < 0 && <span className="block text-xs text-destructive">há {-l.dias} {l.dias === -1 ? "dia" : "dias"}</span>}
    </span>
  );
}

function TabelaAPagar({
  dados, selecao, podeOperar, onAlternar, onPagar,
}: {
  dados: APagarDTO;
  selecao: Record<string, LinhaListaPagarDTO>;
  podeOperar: boolean;
  onAlternar: (linhas: LinhaListaPagarDTO[], marcar: boolean) => void;
  onPagar: (l: LinhaListaPagarDTO) => void;
}) {
  const pagos = dados.lista.situacao === "PAGO";
  const colunas = 9;
  return (
    <div className="hidden md:block rounded-lg border bg-card overflow-x-auto" data-testid="tabela-a-pagar">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50 border-b text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="w-10 px-3 py-2" aria-label="Selecionar" />
            <th className="text-left font-semibold px-3 py-2">{pagos ? "Pago em" : "Vence"}</th>
            <th className="text-left font-semibold px-3 py-2">Documento</th>
            <th className="text-left font-semibold px-3 py-2">Produtor</th>
            <th className="text-left font-semibold px-3 py-2">Carga</th>
            <th className="text-right font-semibold px-3 py-2">kg</th>
            <th className="text-right font-semibold px-3 py-2">{pagos ? "Valor" : "Saldo"}</th>
            <th className="text-left font-semibold px-3 py-2">Status</th>
            <th className="px-3 py-2" aria-label="Ações" />
          </tr>
        </thead>
        <tbody>
          {dados.lista.grupos.map(g => {
            const selecionaveis = g.linhas.filter(l => l.selecionavel);
            const todas = selecionaveis.length > 0 && selecionaveis.every(l => !!selecao[l.alvoId]);
            const titulo = tituloDoGrupo(g, dados);
            return [
              titulo ? (
                <tr key={`g-${g.chave}`} className={cn("border-b bg-muted/30", g.tom === "perigo" && "bg-erro-suave/60")} data-testid={`grupo-${g.chave}`}>
                  <td className="px-3 py-2">
                    {podeOperar && selecionaveis.length > 0 && (
                      <input
                        type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={todas}
                        onChange={e => onAlternar(selecionaveis, e.target.checked)}
                        aria-label={`Selecionar todos de ${titulo}`}
                      />
                    )}
                  </td>
                  <td colSpan={colunas - 4} className={cn("px-3 py-2 text-xs font-semibold", g.tom === "perigo" && "text-destructive", g.tom === "alerta" && "text-atencao")}>
                    {titulo} · {g.n}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs font-semibold tabular-nums whitespace-nowrap" data-testid={`subtotal-${g.chave}`}>{fmtMoedaBR(g.subtotal)}</td>
                  <td colSpan={2} />
                </tr>
              ) : null,
              ...g.linhas.map(l => (
                <tr key={l.alvoId} className={cn("border-b last:border-0 h-14", selecao[l.alvoId] && "bg-primary/5")} data-testid={`linha-pagar-${l.alvoId}`}>
                  <td className="px-3 py-2">
                    {podeOperar && l.selecionavel && (
                      <input
                        type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={!!selecao[l.alvoId]}
                        onChange={e => onAlternar([l], e.target.checked)}
                        aria-label={`Selecionar ${l.documento} de ${l.produtorNome}`}
                        data-testid={`selecionar-${l.alvoId}`}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2"><Vence l={l} /></td>
                  <td className="px-3 py-2">
                    <b className="font-mono font-semibold">{l.documento}</b>
                    <span className="block text-xs text-muted-foreground whitespace-nowrap">
                      {l.data ? fmtDataCurtaBR(l.data) : ""}{l.semConferir && <span className="text-atencao"> · sem conferir</span>}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <AvatarProdutor nome={l.produtorNome} noRizoma={l.noRizoma} tamanho="sm" />
                      <div className="min-w-0">
                        <p className="font-medium truncate max-w-[16rem]">{l.produtorNome}</p>
                        <p className="text-xs text-muted-foreground">{l.natureza === "REGISTRO" ? "registro próprio" : l.natureza === "CARGA" ? "carga que você recebeu" : "no Rizoma"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {l.cargaId && l.cargaIdentificador
                      ? <Link href={`/mercado/cargas/${l.cargaId}`} state={origemAtual()} className="font-mono text-xs text-primary hover:underline">{l.cargaIdentificador}</Link>
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap">{fmtKgBR(l.kg).replace(" kg", "")}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums whitespace-nowrap">{fmtMoedaBR(pagos ? l.valorPago : l.saldo)}</td>
                  <td className="px-3 py-2"><ChipPagamento status={l.status} excedenteValor={l.excedenteValor} /></td>
                  <td className="px-3 py-2 text-right">
                    {podeOperar && l.selecionavel && (
                      <Button size="sm" variant="outline" onClick={() => onPagar(l)} data-testid={`button-registrar-${l.alvoId}`}>
                        Registrar pagamento
                      </Button>
                    )}
                  </td>
                </tr>
              )),
            ];
          })}
        </tbody>
        <tfoot>
          <tr className="border-t bg-muted/40 text-xs">
            <td />
            <td colSpan={4} className="px-3 py-2 text-muted-foreground">{plural(dados.lista.totais.documentos, "documento", "documentos")}</td>
            <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap">{fmtKgBR(dados.lista.totais.kg)}</td>
            <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums whitespace-nowrap" data-testid="total-a-pagar">{fmtMoedaBR(dados.lista.totais.valor)}</td>
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ─── Lista compacta (celular) ────────────────────────────────────────────────

function ListaCompacta({
  dados, selecao, podeOperar, onAlternar, onPagar,
}: {
  dados: APagarDTO;
  selecao: Record<string, LinhaListaPagarDTO>;
  podeOperar: boolean;
  onAlternar: (linhas: LinhaListaPagarDTO[], marcar: boolean) => void;
  onPagar: (l: LinhaListaPagarDTO) => void;
}) {
  const pagos = dados.lista.situacao === "PAGO";
  return (
    <div className="md:hidden rounded-lg border bg-card divide-y" data-testid="lista-a-pagar">
      {dados.lista.grupos.map(g => {
        const titulo = tituloDoGrupo(g, dados);
        return (
          <div key={g.chave}>
            {titulo && (
              <div className={cn("flex items-center justify-between gap-2 bg-muted/40 px-3 py-1.5 text-xs font-semibold", g.tom === "perigo" && "text-destructive", g.tom === "alerta" && "text-atencao")}>
                <span className="truncate">{titulo} · {g.n}</span>
                <span className="font-mono tabular-nums whitespace-nowrap">{fmtMoedaBR(g.subtotal)}</span>
              </div>
            )}
            {g.linhas.map(l => (
              <div key={l.alvoId} className={cn("grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-x-2.5 px-3 py-2.5", selecao[l.alvoId] && "bg-primary/5")} data-testid={`linha-pagar-m-${l.alvoId}`}>
                {podeOperar && l.selecionavel ? (
                  <input
                    type="checkbox" className="h-5 w-5 accent-[hsl(var(--primary))]" checked={!!selecao[l.alvoId]}
                    onChange={e => onAlternar([l], e.target.checked)}
                    aria-label={`Selecionar ${l.documento} de ${l.produtorNome}`}
                  />
                ) : <span />}
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{l.documento} · {l.produtorNome}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {pagos
                      ? `pago ${fmtDataCurtaBR(l.pagoEm)}${l.forma ? ` · ${FORMA_LANCAMENTO_LABEL[l.forma as FormaLancamento] ?? l.forma}` : ""}`
                      : <>
                          {l.vencimento ? `vence ${fmtDataCurtaBR(l.vencimento)}` : "sem vencimento"}
                          {l.dias != null && l.dias < 0 && <span className="text-destructive"> · há {-l.dias} d</span>}
                          {` · ${fmtKgBR(l.kg)}`}
                          {l.semConferir && <span className="text-atencao"> · sem conferir</span>}
                        </>}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-[13px] font-semibold tabular-nums whitespace-nowrap">{fmtMoedaBR(pagos ? l.valorPago : l.saldo)}</p>
                  {podeOperar && l.selecionavel
                    ? <button type="button" className="text-xs font-semibold text-primary" onClick={() => onPagar(l)}>Registrar</button>
                    : pagos ? <StatusChip kind="pago">Pago</StatusChip> : null}
                </div>
              </div>
            ))}
          </div>
        );
      })}
      <div className="flex items-center justify-between px-3 py-2 text-xs text-muted-foreground">
        <span>{plural(dados.lista.totais.documentos, "documento", "documentos")}</span>
        <span className="font-mono font-semibold text-foreground tabular-nums">{fmtMoedaBR(dados.lista.totais.valor)}</span>
      </div>
    </div>
  );
}

// ─── Seleção ─────────────────────────────────────────────────────────────────

/** "3 documentos · R$ 21.684,40" — a soma do que o corretor marcou agora. */
export function textoDaSelecao(linhas: { saldo: number }[]): string {
  const total = linhas.reduce((a, l) => a + l.saldo, 0);
  return `${plural(linhas.length, "documento", "documentos")} · ${fmtMoedaBR(Math.round(total * 100) / 100)}`;
}

function BarraDeSelecao({ linhas, onLimpar, onPagar }: { linhas: LinhaListaPagarDTO[]; onLimpar: () => void; onPagar: () => void }) {
  return (
    // Acima da tabbar no celular (bottom-16); no desktop, no pé da tela.
    <div className="fixed inset-x-3 bottom-16 md:bottom-4 md:left-auto md:right-6 md:w-auto z-40 flex items-center gap-2 rounded-lg border bg-foreground px-3 py-2.5 text-background shadow-lg" data-testid="barra-selecao">
      <span className="text-sm font-medium tabular-nums truncate">{textoDaSelecao(linhas)}</span>
      <Button size="sm" variant="ghost" className="ml-auto text-background hover:bg-background/10" onClick={onLimpar} data-testid="button-limpar-selecao">Limpar</Button>
      <Button size="sm" variant="secondary" onClick={onPagar} data-testid="button-registrar-selecao">
        <Banknote className="h-4 w-4 mr-1" />Registrar pagamento
      </Button>
    </div>
  );
}

// ─── Carga inteira ───────────────────────────────────────────────────────────

function DialogEscolherCarga({
  aberto, cargas, onFechar, onEscolher,
}: {
  aberto: boolean;
  cargas: APagarDTO["cargasComSaldo"];
  onFechar: () => void;
  onEscolher: (c: APagarDTO["cargasComSaldo"][number]) => void;
}) {
  if (!aberto) return null;
  return (
    <Dialog open onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Pagar uma carga inteira</DialogTitle>
          <DialogDescription>Um lançamento por produtor com saldo na carga.</DialogDescription>
        </DialogHeader>
        <ul className="divide-y rounded-md border">
          {cargas.map(c => (
            <li key={c.cargaId}>
              <button type="button" className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-muted/50" onClick={() => onEscolher(c)}>
                <span>
                  <b className="font-mono">{c.identificador}</b>
                  <span className="block text-xs text-muted-foreground">{plural(c.nProdutores, "produtor", "produtores")}</span>
                </span>
                <span className="font-mono text-sm font-semibold tabular-nums">{fmtMoedaBR(c.saldo)}</span>
              </button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Pagar a carga inteira.
 *
 * O diálogo DIZ o que vai ser lançado antes de lançar: são N pagamentos a N
 * pessoas, e um botão que dispara isso sem mostrar o quê seria o tipo de ato que
 * ninguém quer descobrir depois.
 */
function DialogPagarCarga({
  carga, onFechar,
}: {
  carga: APagarDTO["cargasComSaldo"][number] | null;
  onFechar: () => void;
}) {
  const { toast } = useToast();
  const informar = useInformarPagamentoDaCarga(carga?.cargaId);
  const [data, setData] = useState("");

  if (!carga) return null;

  return (
    <Dialog key={carga.cargaId} open onOpenChange={o => !o && onFechar()}>
      <DialogContent
        className="sm:max-w-sm"
        // Hoje NA FAZENDA: `toISOString()` daria amanhã depois das 21h em Irecê.
        onOpenAutoFocus={() => setData(diaCivilNaFazenda(new Date()))}
      >
        <DialogHeader>
          <DialogTitle>Pagar a carga {carga.identificador}</DialogTitle>
          <DialogDescription>
            Um lançamento por produtor com saldo — {plural(carga.nProdutores, "produtor", "produtores")},
            {" "}{fmtMoedaBR(carga.saldo)} no total. {AVISO_PAGAR_CARGA}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="carga-pag-data">Data do pagamento</Label>
          <Input id="carga-pag-data" type="date" value={data} onChange={e => setData(e.target.value)} data-testid="input-carga-pagamento-data" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button
            onClick={() => informar.mutate({ data, forma: "PIX" }, {
              onSuccess: (r) => {
                onFechar();
                toast({ title: plural(r.lancados, "pagamento registrado", "pagamentos registrados"), description: fmtMoedaBR(r.total) });
              },
              onError: (e: Error) => toast({ title: "Não consegui registrar", description: e.message, variant: "destructive" }),
            })}
            disabled={!data || informar.isPending}
            data-testid="button-confirmar-pagar-carga"
          >
            {informar.isPending ? "Registrando…" : "Registrar pagamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function plural(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}
